import { useMemo, useState } from 'react';
import { Banner, Dialog, Icon, Stack, Text, tokens } from '@/design';
import type { MapStateFlags } from './mapModel';

interface MapStateOverlaysProps {
  flags: MapStateFlags;
  permissionDeniedMessage: string | null;
  onRetry(): void;
  onClearPermissionDenied(): void;
  onRequestAddLayer(): void;
}

export function MapStateOverlays({
  flags,
  permissionDeniedMessage,
  onRetry,
  onClearPermissionDenied,
  onRequestAddLayer,
}: MapStateOverlaysProps) {
  const [mixedCrsDismissed, setMixedCrsDismissed] = useState(false);
  const activeBanners = useMemo(() => {
    const banners: Array<{
      key: string;
      severity: 'warning' | 'error';
      title: string;
      body: string;
      icon: 'cloud_off' | 'sync_problem' | 'wrong_location' | 'key';
    }> = [];
    if (flags.offline) {
      banners.push({
        key: 'offline',
        severity: 'error',
        title: 'Offline tiles',
        body: 'Map tile connectivity is unavailable. Retry when network returns.',
        icon: 'cloud_off',
      });
    }
    if (flags.stale || flags.partialLoading) {
      banners.push({
        key: 'stale',
        severity: 'warning',
        title: 'Tile freshness warning',
        body: 'Some tiles are stale or partially loaded.',
        icon: 'sync_problem',
      });
    }
    if (flags.lowAccuracy) {
      banners.push({
        key: 'accuracy',
        severity: 'warning',
        title: 'Low coordinate accuracy',
        body: 'At least one visible layer reports low positional accuracy.',
        icon: 'wrong_location',
      });
    }
    if (flags.expiredCredentials) {
      banners.push({
        key: 'credentials',
        severity: 'error',
        title: 'Layer credentials expired',
        body: 'A connector token expired. Refresh credentials in integrations.',
        icon: 'key',
      });
    }
    return banners;
  }, [flags.expiredCredentials, flags.lowAccuracy, flags.offline, flags.partialLoading, flags.stale]);

  return (
    <>
      {flags.empty ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            pointerEvents: 'none',
            zIndex: tokens.zIndex.panel,
          }}
        >
          <div
            style={{
              pointerEvents: 'auto',
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              background: tokens.color.surface[1],
              padding: tokens.space[4],
              width: tokens.width.popoverMd,
              maxWidth: '100%',
            }}
          >
            <Stack direction="column" gap={2}>
              <Stack direction="row" align="center" gap={1}>
                <Icon name="layers" size={20} decorative />
                <Text variant="subtitle" as="h3" style={{ margin: 0 }}>
                  No layers in view
                </Text>
              </Stack>
              <Text variant="body" as="p" style={{ margin: 0 }}>
                Add a layer to start exploring the map workspace.
              </Text>
              <button
                type="button"
                onClick={onRequestAddLayer}
                style={{
                  appearance: 'none',
                  minHeight: tokens.density.touchMin,
                  borderRadius: tokens.radius.input,
                  border: tokens.border.thin,
                  background: tokens.color.surface[3],
                  color: tokens.color.ink.primary,
                  paddingInline: tokens.space[3],
                  cursor: 'pointer',
                }}
              >
                Add a layer
              </button>
            </Stack>
          </div>
        </div>
      ) : null}

      <div
        style={{
          position: 'absolute',
          left: tokens.space[2],
          right: tokens.space[2],
          bottom: tokens.layout.bottomBarHeight + tokens.space[2],
          display: 'grid',
          gap: tokens.space[2],
          zIndex: tokens.zIndex.panel,
          pointerEvents: 'none',
        }}
      >
        {activeBanners.map((banner) => (
          <div key={banner.key} style={{ pointerEvents: 'auto' }}>
            <Banner
              severity={banner.severity}
              title={banner.title}
              body={
                <Stack direction="row" align="center" gap={2}>
                  <Icon name={banner.icon} size={20} decorative />
                  <span>{banner.body}</span>
                </Stack>
              }
              narrationKey={`gvw-map-${banner.key}`}
              actions={[{ label: 'Retry', onClick: onRetry }]}
            />
          </div>
        ))}
        {permissionDeniedMessage ? (
          <div style={{ pointerEvents: 'auto' }}>
            <Banner
              severity="error"
              title="Permission denied"
              body={permissionDeniedMessage}
              narrationKey="gvw-map-permission-denied"
              actions={[{ label: 'Dismiss', onClick: onClearPermissionDenied }]}
            />
          </div>
        ) : null}
      </div>

      {flags.mixedCrs && !mixedCrsDismissed ? (
        <Dialog
          role="alertdialog"
          title="Mixed CRS or datum mismatch"
          onClose={() => setMixedCrsDismissed(true)}
          actions={[
            {
              label: 'Dismiss',
              variant: 'secondary',
              onClick: () => setMixedCrsDismissed(true),
            },
          ]}
        >
          <Text variant="body" as="p" style={{ margin: 0 }}>
            Visible layers report incompatible coordinate systems. Reproject in GeoConverter for
            highest accuracy.
          </Text>
        </Dialog>
      ) : null}
    </>
  );
}
